import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthUser } from '@core/domain/model/user/auth-user.model';
import { TranslateModule } from '@ngx-translate/core';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '@shared/ui-components/button/button.component';
import { startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'ac-country-selection',
  standalone: true,
  imports: [TranslateModule, MatRadioGroup, MatRadioButton, ReactiveFormsModule, ButtonComponent, AsyncPipe],
  template: `
    <p class="title-m text-secondary-dark">{{ 'COUNTRY_SELECTION.TITLE' | translate }}</p>
    <p class="paragraph-m c-secondary-medium-dark mt-1 s:mt-2">{{ 'COUNTRY_SELECTION.SUBTITLE' | translate }}</p>
    <mat-radio-group [formControl]="_userFormControl">
      @for (user of _sortedUsers; track user.country) {
        <mat-radio-button class="mr-2" [value]="user" [disableRipple]="true">
          {{ 'GENERAL.COUNTRIES.' + user.country | translate }}
        </mat-radio-button>
      }
    </mat-radio-group>
    <div class="mt-3 flex flex-col s:flex-row s:justify-end">
      <button primary-button [disabled]="_notValid$ | async" (click)="_submit()" (keydown.space)="_submit()" (keydown.enter)="_submit()">
        {{ 'GENERAL.ENTER' | translate }}
      </button>
    </div>
  `,
  styles: `
    @use 'functions';
    @use 'mixins/breakpoints';

    :host {
      display: block;
      padding: functions.spacing(2);

      @include breakpoints.mediaBreakpoint('s') {
        padding: functions.spacing(5);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountrySelectionDialog {
  protected readonly _userFormControl;
  protected readonly _notValid$;
  protected readonly _sortedUsers: AuthUser[];

  constructor(
    @Inject(MAT_DIALOG_DATA) protected readonly _users: AuthUser[],
    private readonly _dialogRef: MatDialogRef<CountrySelectionDialog>
  ) {
    if (!this._users || this._users.length === 0) {
      throw new Error('No users provided');
    }
    this._sortedUsers = [...this._users];
    this._sortedUsers.sort((a, b) => a.country.localeCompare(b.country));
    this._userFormControl = new FormControl<AuthUser>(this._sortedUsers[0], { nonNullable: true, validators: Validators.required });
    this._notValid$ = this._userFormControl.statusChanges
      .pipe(startWith(this._userFormControl.status))
      .pipe(map((status) => status !== 'VALID'));
  }

  protected _submit() {
    if (this._userFormControl.valid) {
      this._dialogRef.close(this._userFormControl.value);
    }
  }
}
